import styled from '@emotion/styled/macro';
import { Variables } from '../../base/variables';

const V = Variables.ThemeLight;
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** The editor fills the dialog: header, two columns that scroll on their own, footer. */
/**
 * The editor's frame. It FILLS whatever hosts it (FE-166): core's dialog content is a row flexbox
 * (`StyledDialogContent`, `display: flex`), and a row flex item takes its content's width instead of the row's.
 * Without `flex: 1`, the editor stopped ~80px short of the dialog, with an empty strip beyond it.
 * `min-width: 0` lets it narrow with the dialog rather than push past it and be cut off by the host's
 * `overflow: hidden`; the form column scrolls instead.
 */
export const Editor = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    height: min(86vh, 920px);
    background: ${V.Background3};
`;

export const Header = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 18px 24px 14px;
    border-bottom: 1px solid ${V.Border1};

    > svg {
        flex: none;
        margin-top: 2px;
        color: ${V.PrimaryConstant6};
    }
`;

export const HeaderText = styled.div`
    flex: 1;
    min-width: 0;
`;

export const Title = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const WorkerKey = styled.span`
    padding: 2px 8px;
    border-radius: 6px;
    font-family: ${MONO};
    font-size: 12px;
    font-weight: 500;
    color: ${V.TextMedium};
    background: ${V.Background1};
`;

export const Sub = styled.div`
    margin-top: 4px;
    font-size: 12.5px;
    color: ${V.TextMild};
`;

export const Body = styled.div`
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: 340px minmax(0, 1fr);
`;

export const OrderColumn = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    border-right: 1px solid ${V.Border1};
    background: ${V.Background2};
`;

export const OrderHead = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px 10px;
`;

export const OrderTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${V.TextStrong};

    span {
        margin-left: 6px;
        font-weight: 400;
        color: ${V.TextMild};
    }
`;

export const OrderHint = styled.div`
    font-size: 12px;
    color: ${V.TextMild};
`;

export const OrderList = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 16px 16px;
`;

export const OrderCard = styled.div<{ draft?: boolean; blocking?: boolean }>`
    display: flex;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${({ draft, blocking }) => (draft ? V.PrimaryConstant6 : blocking ? V.AttentionMild3 : V.Border1)};
    background: ${({ draft, blocking }) => (draft ? V.PrimaryMild2 : blocking ? V.AttentionMild3 : V.Background3)};
    cursor: ${({ draft }) => (draft ? 'default' : 'pointer')};
`;

export const OrderNumber = styled.span`
    flex: none;
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid ${V.Border1};
    font-size: 12px;
    color: ${V.TextMedium};
    background: ${V.Background3};
`;

export const OrderText = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const OrderName = styled.div<{ draft?: boolean }>`
    font-size: 13px;
    font-weight: ${({ draft }) => (draft ? 600 : 500)};
    color: ${V.TextStrong};
    overflow-wrap: anywhere;
`;

export const OrderSummary = styled.div`
    font-family: ${MONO};
    font-size: 11.5px;
    color: ${V.TextMild};
    overflow-wrap: anywhere;
`;

export const OrderFlag = styled.div<{ tone: 'primary' | 'warning' | 'attention' }>`
    font-size: 11.5px;
    color: ${({ tone }) =>
        tone === 'primary' ? V.PrimaryInverted3 : tone === 'attention' ? V.AttentionInverted3 : V.WarningInverted4};
`;

export const OrderMove = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const FormColumn = styled.div`
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 24px 24px;
`;

export const Banner = styled.div<{ tone: 'attention' | 'warning' }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 8px;
    font-size: 13px;
    color: ${V.TextStrong};
    background: ${({ tone }) => (tone === 'attention' ? V.AttentionMild3 : V.WarningMild3)};

    > span {
        flex: 1;
    }
`;

export const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const Label = styled.label`
    font-size: 12.5px;
    font-weight: 600;
    color: ${V.TextMedium};

    span {
        margin-left: 6px;
        font-weight: 400;
        color: ${V.TextMild};
    }
`;

export const Hint = styled.div`
    font-size: 12px;
    color: ${V.TextMild};
`;

export const StepHead = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12.5px;
    color: ${V.TextMild};
`;

export const StepTag = styled.span<{ then?: boolean }>`
    padding: 3px 8px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: ${V.Background3};
    background: ${({ then }) => (then ? V.PrimaryConstant6 : V.NeutralConstant6)};
`;

export const Block = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 14px 16px;
    border-radius: 8px;
    border: 1px solid ${V.Border1};
    background: ${V.Background3};
`;

export const Joiner = styled.div`
    align-self: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: ${V.TextMild};
`;

export const Toggles = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;


export const TwoColumns = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
`;

export const ChipField = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-height: 40px;
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px dashed ${V.Border2};
    background: ${V.Background3};

    input {
        flex: 1;
        min-width: 120px;
        border: none;
        outline: none;
        background: transparent;
        font-size: 13px;
        color: ${V.TextStrong};
    }
`;

export const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    padding: 3px 8px;
    border-radius: 6px;
    font-family: ${MONO};
    font-size: 12.5px;
    color: ${V.TextStrong};
    background: ${V.Background1};
    overflow-wrap: anywhere;

    /* The remove control is core BaseButton; only its size is set here, to sit inside the chip. */
    button {
        min-width: 0;
        height: auto;
        padding: 0;
    }
`;

export const Kept = styled.div`
    font-size: 12px;
    color: ${V.TextMild};

    strong {
        color: ${V.TextMedium};
    }
`;

export const Code = styled.pre`
    margin: 0;
    max-height: 220px;
    overflow: auto;
    padding: 10px 12px;
    border-radius: 6px;
    font-family: ${MONO};
    font-size: 12px;
    color: ${V.TextStrong};
    background: ${V.Background1};
`;

export const Footer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 24px;
    border-top: 1px solid ${V.Border1};
`;

export const Preview = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${V.TextMedium};

    svg {
        color: ${V.PrimaryConstant6};
    }
`;

export const ErrorText = styled.div`
    font-size: 12.5px;
    color: ${V.AttentionInverted3};
`;

/** Carries the reason a disabled control cannot show itself. */
export const Slot = styled.span`
    display: inline-flex;
`;
